<template>
  <el-dialog
    :visible="true"
    v-bind="$attrs"
    title="範本包含的通知對象"
    width="566px"
    :close-on-click-modal="false"
    @close="$emit('close')"
  >
    <p class="text-gray-80 text-sm mb-[12px]">如需更動範本內的設定，請至「通知模組 > 通知範本設定 > 通知對象範本」</p>
    <GrayBlockContainer class="mb-[40px]">
      <p class="text-sub text-gray-100 font-medium mb-[8px]">通知對象範本A</p>
      <BaseElForm label-position="left" label-width="90px">
        <!-- 服務項目範本 -->
        <BaseElFormItem v-if="servicesList.length" label="服務項目">
          <div>{{ servicesList }}</div>
        </BaseElFormItem>
        <BaseElFormItem v-if="attachServicesList.length" label="附加服務">
          <div>{{ attachServicesList }}</div>
        </BaseElFormItem>

        <template v-if="showControl.riskLevel">
          <!-- 如果同時有服務項目和風險層級，顯示分隔線 -->
          <el-divider v-if="servicesList.length" class="my-5">
            <Tag type="primary" size="small" disable-transitions>and</Tag>
          </el-divider>
          <!-- 風險層級範本 -->
          <BaseElFormItem label="訂單風險">
            <div>{{ riskLevelList }}</div>
          </BaseElFormItem>
        </template>
      </BaseElForm>
    </GrayBlockContainer>

    <div class="flex justify-end items-center">
      <BaseElButton type="primary" @click="$emit('close')">確認</BaseElButton>
    </div>
  </el-dialog>
</template>

<script>
import { defineComponent, computed, toRef } from 'vue'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import { useProgressNotificationTarget } from '@/use/useProgressNotificationTarget'
import { map, join, get, find, isEmpty } from 'lodash'
import { appointmentRiskGuardConfig } from '@/config/appointmentRiskGuard'
import Tag from '@/components/Tag/Tag.vue'

export default defineComponent({
  name: 'TargetNotificationSelectModal',
  components: {
    GrayBlockContainer,
    Tag,
  },
  props: {
    targetTemplate: { type: Object, default: () => ({}) },
    reservationServicesData: { type: Object, default: () => ({}) },
  },
  setup (props, { emit }) {
    const { categoryServiceDataList, serviceDataList, attachServiceDataList, getDataByPath } = useProgressNotificationTarget({ reservationServicesData: toRef(props, 'reservationServicesData') })
    const showControl = computed(() => {
      const controls = {
        riskLevel: !isEmpty(get(props.targetTemplate, 'appointmentConfig.riskLevelConditions')),
      }
      return controls
    })

    // 風險層級列表
    const riskLevelList = computed(() => {
      if (!showControl.value.riskLevel) return ''
      const riskLevelConditions = get(props.targetTemplate, 'appointmentConfig.riskLevelConditions')
      if (!riskLevelConditions) return '未設定'

      const levels = Array.isArray(riskLevelConditions) ? riskLevelConditions : [riskLevelConditions]
      const labels = levels.map(level => {
        const config = appointmentRiskGuardConfig.riskLevels[level]
        return config ? `${config.label}風險` : (level || '未設定')
      })
      return labels.join('、')
    })

    // 服務列表
    const servicesList = computed(() => {
      const serviceSettings = get(props.targetTemplate, 'appointmentConfig.serviceSettings')
      const subServiceSettings = get(props.targetTemplate, 'appointmentConfig.subServiceSettings')

      const includeServices = map(get(serviceSettings, 'includeServiceIds'), serviceId => {
        return find(getDataByPath(categoryServiceDataList.value, 'services'), { id: serviceId }) || find(serviceDataList.value, { id: serviceId })
      }).filter(Boolean)
      const subServices = map(get(subServiceSettings, 'subServiceIds'), serviceId => {
        return find(getDataByPath(serviceDataList.value, 'subServices'), { id: serviceId })
      }).filter(Boolean)

      const result = [...includeServices, ...subServices]
      return map(result, item => item?.name).join('、')
    })

    // 附加服務列表
    const attachServicesList = computed(() => {
      const servicesData = get(props.reservationServicesData, 'appointmentServiceAttaches')
      return join(
        map(
          get(props.targetTemplate, 'appointmentConfig.serviceAttachSettings.serviceAttachIds'),
          (id) => {
            const target = find(servicesData, { id })
            return get(target, 'name')
          },
        ),
        '、')
    })

    return {
      showControl,
      servicesList,
      attachServicesList,
      riskLevelList,
    }
  },
})
</script>

<style lang="postcss" scoped>
::v-deep .el-dialog__body {
  @apply pt-0;
}
::v-deep .el-form-item {
  @apply mb-0 ;
}

::v-deep .el-form-item__label {
  @apply text-gray-60 text-sm font-medium float-left leading-[20.27px] pb-[4px];
}
::v-deep .el-form-item__content {
  @apply text-gray-60 text-sm leading-[20.27px];
}
::v-deep .el-divider__text {
  @apply bg-[#F5F7FA] px-2 pt-2;
}
</style>
